/**
 * VUKZ Financial Assumptions — Source of Truth
 * All investor/valuation pages derive from these numbers.
 * Change here → reflects everywhere.
 */

export const COMPANY_INFO = {
  name: 'VUKZ.ai',
  legalName: 'VUKZ Tecnologia Ltda.',
  cnpj: 'XX.XXX.XXX/0001-XX', // placeholder
  founded: '2024',
  hq: 'Belo Horizonte, MG — Brasil',
  founder: 'Paulo Brant',
  founderTitle: 'Founder & CEO',
  website: 'https://vukz.ai',
  stage: 'Pre-Seed / Early Revenue',
  sector: 'Sports Tech / AI / Computer Vision',
};

export const FINANCIAL_ASSUMPTIONS = {
  currency: 'BRL',
  fxRate: 5.20, // USD 1 = R$ 5.20
  churn: { coaches: 0.04, clubs: 0.02, enterprise: 0.01 },
  cac: { coaches: 500, clubs: 2000, enterprise: 10000 },
  ltvCacTarget: 4,
  grossMargin: { saas: 0.78, vision: 0.65, marketplace: 0.85 },
  taxOnRevenue: 0.0933, // Simples Nacional
  annualInflation: 0.06,
  annualPriceIncrease: 0.08,
  contractMinMonths: 6,
  paymentTermDays: 30,
};

export const AZURE_ASSUMPTIONS = {
  creditsUSD: 250000,
  creditsBRL: 250000 * 5.20,
  estimatedBurnMonths: 24,
  monthlyBaseCost: 2500, // BRL current
  gpuCostPerVideo: 0.50, // BRL
  scaledMonthlyCost: { y1: 4000, y2: 8000, y3: 15000, y4: 25000, y5: 40000 },
  microsoftForStartups: true,
  azureAIFoundry: true,
};

export const HEADCOUNT_PLAN = {
  current: { devs: 1, sales: 0, cs: 0, design: 0, total: 1 },
  seed: { devs: 3, sales: 1, cs: 1, design: 0, total: 6 },
  seriesA: { devs: 8, sales: 3, cs: 2, design: 2, total: 15 },
  avgSalary: { dev: 15000, sales: 10000, cs: 8000, design: 12000 }, // BRL/month
};

export const MARKET_SIZE = {
  tam: { label: 'Sports Tech Software BR', valueBRL: 500_000_000, description: 'Software de gestão + analytics esportivo Brasil' },
  sam: { label: 'Segmentos-alvo VUKZ', valueBRL: 80_000_000, description: 'Clubes + treinadores + federações com budget tech' },
  som: { label: 'Capturável 5 anos', valueBRL: 30_000_000, description: 'R$ 15M–40M ARR com execução' },
  segments: [
    { name: 'Treinadores (solo)', count: 30000, ticketAvg: 350, penetrationY5: 0.03 },
    { name: 'Clubes pequenos', count: 15000, ticketAvg: 800, penetrationY5: 0.02 },
    { name: 'Clubes médios/grandes', count: 5000, ticketAvg: 3500, penetrationY5: 0.03 },
    { name: 'Federações/Confederações', count: 200, ticketAvg: 10000, penetrationY5: 0.10 },
    { name: 'Academias (B2B)', count: 35000, ticketAvg: 1500, penetrationY5: 0.01 },
  ],
};

export const COMPETITORS = [
  { name: 'Hudl', focus: 'Video analysis (US)', valuation: '$4B', threat: 'low', reason: 'Preço 50x maior, não atua no Brasil' },
  { name: 'Catapult Sports', focus: 'Wearables + analytics', valuation: '$1.3B', threat: 'low', reason: 'Hardware-first, mercado diferente' },
  { name: 'Sportlogiq', focus: 'AI sports analytics', valuation: '~$100M', threat: 'medium', reason: 'AI-first mas foco hockey/soccer US' },
  { name: 'Stats Perform / Opta', focus: 'Sports data', valuation: '$800M', threat: 'low', reason: 'Dados de broadcast, não B2B/clubes' },
  { name: 'Metrica Sports', focus: 'Tracking + tactical', valuation: '~$30M', threat: 'medium', reason: 'Tracking video mas sem multi-sport AI' },
  { name: 'Spiideo', focus: 'Video platform', valuation: '~$20M', threat: 'medium', reason: 'Video recording, não analytics AI' },
  { name: 'Empresas BR (planilhas/WhatsApp)', focus: 'Manual', valuation: '—', threat: 'high', reason: 'Status quo: maior "concorrente"' },
];

export const DIFFERENTIATORS = [
  { id: 'cv', title: 'Visão Computacional Multi-esporte', description: 'TrackVision + SwimVision — biomecânica automatizada por vídeo para natação, atletismo, futebol, ginástica, vôlei e mais', moat: 'high' },
  { id: 'agents', title: '35+ AI Agents', description: 'Pipeline multi-agent (Parser→Sentinel→Commit) + agentes de produto. Vendáveis no Microsoft Marketplace.', moat: 'high' },
  { id: 'identity', title: 'Identity Resolution', description: 'Fuzzy matching ≥92% + grafo canônico. Resolve duplicatas entre fontes automaticamente.', moat: 'medium' },
  { id: 'multisport', title: 'Arquitetura Multi-esporte', description: 'Schema extensível. Natação + atletismo em produção. Futebol, ginástica, vôlei, surf, crossfit no roadmap.', moat: 'medium' },
  { id: 'governance', title: 'Governança Built-in', description: 'Purview, LGPD, RLS, FinOps desde o dia 1. Raro em startups early-stage.', moat: 'medium' },
  { id: 'microsoft', title: 'Ecossistema Microsoft', description: 'Azure AI Foundry, Microsoft for Startups ($250K credits), Copilot extensibility, AI agents marketplace.', moat: 'high' },
];

export const RISKS = [
  { id: 'gpu-cost', title: 'Custo GPU escalar', probability: 'alta', impact: 'médio', mitigation: 'Batch processing, cache modelos, preço/vídeo cobre custo marginal' },
  { id: 'competition', title: 'Competição internacional', probability: 'média', impact: 'médio', mitigation: 'Nicho BR/LATAM, preço 10x menor, multi-sport, localização' },
  { id: 'key-person', title: 'Founder solo (key-person)', probability: 'alta', impact: 'alto', mitigation: 'Documentação extensa, contratação com Seed, AI-assisted development' },
  { id: 'churn', title: 'Churn de coaches', probability: 'média', impact: 'médio', mitigation: 'Onboarding agent, conteúdo educativo, community, lock-in por dados' },
  { id: 'lgpd', title: 'LGPD (menores de idade)', probability: 'média', impact: 'alto', mitigation: 'Purview governance, consent flows implementados, auditoria contínua' },
  { id: 'sales-cycle', title: 'Ciclo de venda longo (clubes)', probability: 'alta', impact: 'médio', mitigation: 'Pilotos grátis 3 meses, bottom-up (coach→club), referral program' },
  { id: 'azure-lock', title: 'Dependência Azure', probability: 'baixa', impact: 'médio', mitigation: 'Docker/multi-cloud ready, AI agents portáveis, dados exportáveis' },
];
